/*
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT
 *
 * Licensed under the MIT License. See the LICENSE accompanying this file
 * for the specific language governing permissions and limitations under
 * the License.
 */

import React from 'react';

import { makeStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';

import { VpnKey, Visibility, VisibilityOff } from '@material-ui/icons';

import { Branding } from '../../branding';

const useStyles = makeStyles(() => ({
    formControl: {
        width: '100%',
    },
    input: {
        width: '100%',
    },
    outlinedRoot: {
        width: '100%',
    },
    textFieldIcon: {
        color: Branding.secondary,
    },
}));

/**
 * パスワード入力フィールドコンポーネント
 * 表示/非表示トグル機能付き
 * @param {'standard'|'outlined'} [variant] — outlined は四角枠（Settings メール欄と同様）
 */
const PasswordInputField = ({
    id,
    label,
    placeholder,
    value,
    showPassword,
    onChange,
    onToggleShowPassword,
    autoFocus = false,
    inputClassName,
    formControlClassName,
    variant = 'standard',
}) => {
    const classes = useStyles();

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const endAdornment = (
        <InputAdornment position="end">
            <IconButton
                aria-label="toggle password visibility"
                onClick={onToggleShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
            >
                {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
        </InputAdornment>
    );

    if (variant === 'outlined') {
        return (
            <TextField
                fullWidth
                variant="outlined"
                id={id}
                label={label}
                placeholder={placeholder}
                value={value}
                type={showPassword ? 'text' : 'password'}
                onChange={(e) => onChange(e.target.value)}
                autoFocus={autoFocus}
                className={formControlClassName || classes.outlinedRoot}
				InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <VpnKey className={classes.textFieldIcon} />
                        </InputAdornment>
                    ),
                    endAdornment,
                }}
            />
        );
    }

    return (
        <FormControl className={formControlClassName || classes.formControl}>
            <InputLabel htmlFor={id}>
                {label}
            </InputLabel>
            <Input
                id={id}
                value={value}
                type={showPassword ? 'text' : 'password'}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                startAdornment={
                    <InputAdornment position="start">
                        <VpnKey className={classes.textFieldIcon} />
                    </InputAdornment>
                }
                endAdornment={endAdornment}
                className={inputClassName || classes.input}
                autoFocus={autoFocus}
                inputProps={{ style: { left: 0 } }}
            />
        </FormControl>
    );
};

/**
 * パスワード一致チェック関数
 * @param {string} newPassword - 新しいパスワード
 * @param {string} confirmPassword - 確認用パスワード
 * @returns {boolean} パスワードが一致し、かつ空でない場合はtrue
 */
export const isPasswordMatch = (newPassword, confirmPassword) => {
    return newPassword.length > 0 &&
        confirmPassword.length > 0 &&
        newPassword === confirmPassword;
};

/**
 * Cognitoで許可されている特殊文字
 * https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html
 */
const COGNITO_SPECIAL_CHARACTERS = '^$*.[]{}()?\"!@#%&/\\,><\':;|_~`=+-';

/**
 * パスワードルール定数
 */
export const PASSWORD_RULES = {
    MIN_LENGTH: 8,
    MAX_LENGTH: 15,
    MIN_CHARACTER_TYPES: 2,
    SPECIAL_CHARACTERS: COGNITO_SPECIAL_CHARACTERS,
};

/**
 * パスワードに含まれる文字種別をカウント
 * @param {string} password - パスワード
 * @returns {object} 各文字種別の含有状況
 */
const countCharacterTypes = (password) => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    // 特殊文字のチェック（正規表現で使用される文字をエスケープ）
    const specialCharRegex = new RegExp('[' + COGNITO_SPECIAL_CHARACTERS.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&') + ']');
    const hasSpecialChars = specialCharRegex.test(password);

    return {
        hasUppercase,
        hasLowercase,
        hasNumbers,
        hasSpecialChars,
        count: [hasUppercase, hasLowercase, hasNumbers, hasSpecialChars].filter(Boolean).length,
    };
};

/**
 * パスワードバリデーション結果
 * @typedef {Object} PasswordValidationResult
 * @property {boolean} isValid - 全てのルールを満たしているか
 * @property {Object} errors - 各ルールのエラー状態
 * @property {boolean} errors.length - 長さルール違反
 * @property {boolean} errors.characterTypes - 文字種別ルール違反
 * @property {string[]} messages - エラーメッセージの配列
 */

/**
 * パスワードルールのバリデーション
 * ルール:
 * - 8文字〜15文字
 * - 英大文字・小文字・数字・記号の中から2種類以上
 * 
 * @param {string} password - バリデーション対象のパスワード
 * @returns {PasswordValidationResult} バリデーション結果
 */
export const validatePasswordRules = (password) => {
    const errors = {
        length: false,
        characterTypes: false,
    };
    const messages = [];

    // 長さチェック（8〜15文字）
    if (password.length < PASSWORD_RULES.MIN_LENGTH || password.length > PASSWORD_RULES.MAX_LENGTH) {
        errors.length = true;
        messages.push(`パスワードは${PASSWORD_RULES.MIN_LENGTH}文字以上${PASSWORD_RULES.MAX_LENGTH}文字以下で入力してください`);
    }

    // 文字種別チェック（2種類以上）
    const charTypes = countCharacterTypes(password);
    if (charTypes.count < PASSWORD_RULES.MIN_CHARACTER_TYPES) {
        errors.characterTypes = true;
        messages.push('英大文字・小文字・数字・記号の中から2種類以上を含めてください');
    }

    return {
        isValid: !errors.length && !errors.characterTypes,
        errors,
        messages,
        characterTypes: charTypes,
    };
};

/**
 * パスワードが有効かどうかをチェック（シンプル版）
 * @param {string} password - パスワード
 * @returns {boolean} パスワードが全てのルールを満たしている場合はtrue
 */
export const isPasswordValid = (password) => {
    if (!password) return false;
    return validatePasswordRules(password).isValid;
};

/**
 * パスワード一致かつルール適合チェック関数
 * @param {string} newPassword - 新しいパスワード
 * @param {string} confirmPassword - 確認用パスワード
 * @returns {boolean} パスワードがルールを満たし、一致している場合はtrue
 */
export const isPasswordValidAndMatch = (newPassword, confirmPassword) => {
    return isPasswordValid(newPassword) && isPasswordMatch(newPassword, confirmPassword);
};

export default PasswordInputField;

